import alertErrorSmallTokens from '@amzn/meridian-tokens/base/icon/alert-error-small';
import alertWarningSmallTokens from '@amzn/meridian-tokens/base/icon/alert-warning-small';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import React, { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Text, TranslatedString, TranslationFunction } from '../../../components/blocks/localization';
import { FilterTag, FilterTags, Tile } from '../../../components/composites';
import { HelpPopover, PaginatedTable } from '../../../components/constructed';
import {
    AsinToImageUrlMap,
    IAsynchronouslyLoadedValue,
    PoReviewRow,
    ResellerItem,
    ResellerPriceList,
    TableData,
} from '../../../redux/types';
import { ExtractionFinding } from '../../../types';
import { lineItemsNotice, lineItemsNoticeMessage, scannedDocumentNotice } from '../poReviewFindings';
import { isNeutralAddedRow, MATCHES_STATUS, PoReviewRowStatus, rowStatus } from '../poReviewValidation';
import {
    dedupedBasePriceByItemId,
    extendedPriceForRow,
    flattenRows,
    formattedEstimatedSubtotal,
    PoReviewTableRow,
    tableRowKey,
    usePoReviewColumns,
} from './poReviewColumns';
import { PoReviewStatusFilter, STATUS_FILTER_OPTIONS } from './PoReviewStatusFilter';

const ASCENDING = 'ascending';

const STATUS_COLUMN_ID = 'status';

const LINE_ITEMS_PER_PAGE = 20;

type RowComparator = (left: PoReviewRow, right: PoReviewRow, sign: number) => number;

const compareStrings = (left: string, right: string, sign: number): number => sign * left.localeCompare(right);

// Absent numbers sort last whichever way the column is sorted, so an unpriced or unfilled line does not
// jump to the top when the direction flips.
const compareNumbers = (left: number, right: number, sign: number): number => {
    const leftIsNumber = !Number.isNaN(left);
    const rightIsNumber = !Number.isNaN(right);
    if (!leftIsNumber && !rightIsNumber) {
        return 0;
    }
    if (!leftIsNumber) {
        return 1;
    }
    if (!rightIsNumber) {
        return -1;
    }
    return sign * (left - right);
};

const quantityValue = (row: PoReviewRow) => (row.quantity === '' ? NaN : Number(row.quantity));

const STATUS_SORT_RANK: Record<PoReviewRowStatus, number> = {
    [PoReviewRowStatus.ERROR]: 0,
    [PoReviewRowStatus.WARNING]: 1,
    [PoReviewRowStatus.OK]: 2,
};

// The order the table opens in: errors first, then needs-review, then ok, ties in document order. Taken
// once when the tile mounts, so editing a line never moves it.
const initialStatusOrder = (rows: PoReviewRow[]): string[] =>
    [...rows]
        .sort(
            (left, right) =>
                STATUS_SORT_RANK[rowStatus(left)] - STATUS_SORT_RANK[rowStatus(right)] ||
                left.itemNumber - right.itemNumber
        )
        .map((row) => row.rowId);

const filterRowsByStatus = (
    rows: PoReviewRow[],
    selectedStatuses: PoReviewRowStatus[],
    submitAttempted: boolean
): PoReviewRow[] => {
    if (selectedStatuses.length === 0) {
        return rows;
    }
    return rows.filter(
        (row) =>
            !isNeutralAddedRow(row, submitAttempted) && selectedStatuses.some((status) => MATCHES_STATUS[status](row))
    );
};

const buildStatusFilterTags = (
    selectedStatuses: PoReviewRowStatus[],
    t: TranslationFunction,
    onChange: (statuses: PoReviewRowStatus[]) => void
): FilterTag[] => {
    const selected = new Set(selectedStatuses);
    return STATUS_FILTER_OPTIONS.filter((option) => selected.has(option.status)).map((option) => ({
        label: t(option.labelKey),
        key: option.status,
        remove: () => onChange(selectedStatuses.filter((status) => status !== option.status)),
    }));
};

const TableNoticeRow: FC<{ message: TranslatedString; dataTestId?: string }> = ({ message, dataTestId }) => (
    <Row width={'100%'} spacing={'xsmall'} spacingInset={'small'} alignmentVertical={'top'} data-testid={dataTestId}>
        <Icon tokens={alertWarningSmallTokens} />
        <Text type={'b200'}>{message}</Text>
    </Row>
);

const EmptyTableBody: FC<{ message: TranslatedString; isError?: boolean; dataTestId?: string }> = ({
    message,
    isError,
    dataTestId,
}) => (
    <Row width={'100%'} spacing={'xsmall'} spacingInset={'small'} alignmentVertical={'top'} data-testid={dataTestId}>
        {isError && <Icon tokens={alertErrorSmallTokens} />}
        <Text type={'b200'} color={isError ? 'error' : 'primary'}>
            {message}
        </Text>
    </Row>
);

export type PoReviewLineItemsTileProps = {
    t: TranslationFunction;
    rows: PoReviewRow[];
    // Findings about the line items as a whole, which belong to no single line.
    tableFindings: ExtractionFinding[];
    // True when the document is a scanned image, so the page shows the scanned-document notice.
    isScanned: boolean;
    submitAttempted: boolean;
    tableResetCount: number;
    itemMap: Map<string, ResellerItem>;
    priceList: ResellerPriceList | undefined;
    asinToImageUrls: IAsynchronouslyLoadedValue<AsinToImageUrlMap>;
    onItemChange: (rowId: string, itemId?: string) => void;
    onQuantityChange: (rowId: string, quantity: string) => void;
    onAddLine: () => void;
    onRemoveLine: (rowId: string) => void;
    dataTestId: string;
};

const PoReviewLineItemsTile: FC<PoReviewLineItemsTileProps> = ({
    t,
    rows,
    tableFindings,
    isScanned,
    submitAttempted,
    tableResetCount,
    itemMap,
    priceList,
    asinToImageUrls,
    onItemChange,
    onQuantityChange,
    onAddLine,
    onRemoveLine,
    dataTestId,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState('');
    const [sortDirection, setSortDirection] = useState(ASCENDING);
    // A snapshot of the row order: the status order at first load, replaced when the reviewer clicks a
    // column header. Editing a row never reorders it.
    const [orderedRowIds, setOrderedRowIds] = useState<string[]>(() => initialStatusOrder(rows));
    const [selectedStatuses, setSelectedStatuses] = useState<PoReviewRowStatus[]>([]);

    const onPageChange = useCallback((value: string) => {
        const page = parseInt(value, 10);
        if (page) {
            setCurrentPage(page);
        }
    }, []);

    const onStatusFilterChange = useCallback((statuses: PoReviewRowStatus[]) => {
        setSelectedStatuses(statuses);
        setCurrentPage(1);
    }, []);

    const rowsForTableReset = useRef(rows);
    rowsForTableReset.current = rows;

    useLayoutEffect(() => {
        if (tableResetCount > 0) {
            setSelectedStatuses([]);
            setSortColumn(STATUS_COLUMN_ID);
            setSortDirection(ASCENDING);
            setOrderedRowIds(initialStatusOrder(rowsForTableReset.current));
            setCurrentPage(1);
        }
    }, [tableResetCount]);

    const basePriceByItemId = useMemo(() => dedupedBasePriceByItemId(priceList), [priceList]);

    const comparators = useMemo<Record<string, RowComparator>>(() => {
        const stringOf = (itemId: string | undefined, pick: (item: ResellerItem) => string) =>
            itemId ? pick(itemMap.get(itemId) ?? ({} as ResellerItem)) ?? '' : '';
        return {
            lineNumber: (left, right, sign) => compareNumbers(left.itemNumber, right.itemNumber, sign),
            sku: (left, right, sign) =>
                compareStrings(
                    stringOf(left.itemId, (item) => item.externalSku),
                    stringOf(right.itemId, (item) => item.externalSku),
                    sign
                ),
            upc: (left, right, sign) =>
                compareStrings(
                    stringOf(left.itemId, (item) => item.upc),
                    stringOf(right.itemId, (item) => item.upc),
                    sign
                ),
            asin: (left, right, sign) =>
                compareStrings(
                    stringOf(left.itemId, (item) => item.asin),
                    stringOf(right.itemId, (item) => item.asin),
                    sign
                ),
            item: (left, right, sign) =>
                compareStrings(
                    stringOf(left.itemId, (item) => item.title),
                    stringOf(right.itemId, (item) => item.title),
                    sign
                ),
            productDescription: (left, right, sign) =>
                compareStrings(
                    stringOf(left.itemId, (item) => item.title),
                    stringOf(right.itemId, (item) => item.title),
                    sign
                ),
            quantity: (left, right, sign) => compareNumbers(quantityValue(left), quantityValue(right), sign),
            unitPrice: (left, right, sign) =>
                compareNumbers(
                    basePriceByItemId.get(left.itemId ?? '') ?? NaN,
                    basePriceByItemId.get(right.itemId ?? '') ?? NaN,
                    sign
                ),
            extendedPrice: (left, right, sign) =>
                compareNumbers(
                    extendedPriceForRow(basePriceByItemId, left) ?? NaN,
                    extendedPriceForRow(basePriceByItemId, right) ?? NaN,
                    sign
                ),
            status: (left, right, sign) => {
                const leftIsNeutral = isNeutralAddedRow(left, submitAttempted);
                const rightIsNeutral = isNeutralAddedRow(right, submitAttempted);
                if (leftIsNeutral || rightIsNeutral) {
                    return Number(leftIsNeutral) - Number(rightIsNeutral);
                }
                return compareNumbers(STATUS_SORT_RANK[rowStatus(left)], STATUS_SORT_RANK[rowStatus(right)], sign);
            },
        };
    }, [itemMap, basePriceByItemId, submitAttempted]);

    const defaultOrder = useCallback(
        (left: PoReviewRow, right: PoReviewRow) =>
            Number(left.isAdded) - Number(right.isAdded) || left.itemNumber - right.itemNumber,
        []
    );

    // The direction applies to the chosen column only; the line-number tie-break stays ascending so a
    // descending sort does not also flip tied lines.
    const orderForColumn = useCallback(
        (column: string, direction: string): string[] => {
            const comparator = comparators[column];
            const sign = direction === ASCENDING ? 1 : -1;
            const byLineNumber = (left: PoReviewRow, right: PoReviewRow) => left.itemNumber - right.itemNumber;
            const compare = comparator
                ? (left: PoReviewRow, right: PoReviewRow) => comparator(left, right, sign) || byLineNumber(left, right)
                : defaultOrder;
            return [...rows].sort(compare).map((row) => row.rowId);
        },
        [comparators, defaultOrder, rows]
    );

    const onSort = useCallback(
        (sort: { sortColumn: string; sortDirection: string }) => {
            setSortColumn(sort.sortColumn);
            setSortDirection(sort.sortDirection);
            setOrderedRowIds(orderForColumn(sort.sortColumn, sort.sortDirection));
        },
        [orderForColumn]
    );

    const filteredRows = useMemo(() => filterRowsByStatus(rows, selectedStatuses, submitAttempted), [
        rows,
        selectedStatuses,
        submitAttempted,
    ]);

    const sortedRows = useMemo(() => {
        const rowsById = new Map(filteredRows.map((row) => [row.rowId, row]));
        const held = orderedRowIds
            .map((rowId) => rowsById.get(rowId))
            .filter((row): row is PoReviewRow => row !== undefined);
        const heldIds = new Set(orderedRowIds);
        const appended = filteredRows.filter((row) => !heldIds.has(row.rowId)).sort(defaultOrder);
        return [...held, ...appended];
    }, [filteredRows, orderedRowIds, defaultOrder]);

    const columns = usePoReviewColumns({
        itemMap: itemMap,
        priceList: priceList,
        asinToImageUrls: asinToImageUrls,
        submitAttempted: submitAttempted,
        onItemChange: onItemChange,
        onQuantityChange: onQuantityChange,
        onRemoveLine: onRemoveLine,
        dataTestIdPrefix: dataTestId,
    });

    // Adding a line clears any active status filter so the new line is never hidden, then pages to it.
    const handleAddLine = useCallback(() => {
        onAddLine();
        setSelectedStatuses([]);
        setCurrentPage(Math.ceil((rows.length + 1) / LINE_ITEMS_PER_PAGE));
    }, [onAddLine, rows.length]);

    // A removed line can empty the current page, so the page is clamped to the last one that still has
    // lines.
    const pageCount = Math.max(1, Math.ceil(sortedRows.length / LINE_ITEMS_PER_PAGE));
    const page = Math.min(currentPage, pageCount);

    const pageRows = sortedRows.slice((page - 1) * LINE_ITEMS_PER_PAGE, page * LINE_ITEMS_PER_PAGE);
    const table = new TableData<PoReviewTableRow, keyof PoReviewTableRow>(flattenRows(pageRows), columns);

    const formattedSubtotal = formattedEstimatedSubtotal(rows, priceList);

    const tableNoticeMessage = lineItemsNoticeMessage(t, lineItemsNotice(tableFindings));

    const statusFilterTags = buildStatusFilterTags(selectedStatuses, t, onStatusFilterChange);
    const noMatchingLines = selectedStatuses.length > 0 && filteredRows.length === 0;
    const noLines = selectedStatuses.length === 0 && rows.length === 0;

    return (
        <Tile title={t('lineItems-tableTitle')} width={'100%'} contentSpacingInset={'none'} dataTestId={dataTestId}>
            <Column width={'100%'} spacing={'none'}>
                {isScanned && (
                    <TableNoticeRow message={scannedDocumentNotice(t)} dataTestId={`${dataTestId}.scannedNotice`} />
                )}
                {tableNoticeMessage && <TableNoticeRow message={tableNoticeMessage} />}
                <PaginatedTable<PoReviewTableRow>
                    data={table}
                    dataTestId={`${dataTestId}.table`}
                    currentPage={page}
                    numberOfPages={pageCount}
                    onPageChange={onPageChange}
                    onSort={onSort}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    rowKey={(_row, rowData) => tableRowKey(rowData)}
                    isChildRow={(rowData) => rowData.isDetail}
                    cellColumnSpan={(rowData, columnIndex) =>
                        rowData.isDetail && columnIndex === 0 ? columns.length : undefined
                    }
                    layout={'stacked'}
                    inlineFilterActions={true}
                    filterTags={<FilterTags tags={statusFilterTags} t={t} />}
                >
                    <PoReviewStatusFilter
                        t={t}
                        selectedStatuses={selectedStatuses}
                        onChange={onStatusFilterChange}
                        lineCount={filteredRows.length}
                        dataTestId={dataTestId}
                    />
                </PaginatedTable>
                {noMatchingLines && <EmptyTableBody message={t('noMatchingLines-text')} />}
                {noLines && (
                    <EmptyTableBody
                        message={t('addLineItems-text')}
                        isError={submitAttempted}
                        dataTestId={`${dataTestId}.addLineItemsNotice`}
                    />
                )}
                <Row width={'100%'} spacingInset={'small'} alignmentHorizontal={'justify'} alignmentVertical={'top'}>
                    <Button type={'link'} onClick={handleAddLine} data-testid={`${dataTestId}.addLineButton`}>
                        {t('addLine-buttonLabel')}
                    </Button>
                    <Column alignmentHorizontal={'right'} spacing={'xsmall'}>
                        <Row spacing={'xsmall'} alignmentVertical={'center'}>
                            <Text type={'h100'}>{t('estimatedSubtotal-label')}</Text>
                            <HelpPopover
                                header={t('estimatedSubtotal-label')}
                                text={t('estimatedSubtotalHelp-text')}
                                dataTestId={`${dataTestId}.subtotalHelp`}
                            />
                        </Row>
                        {formattedSubtotal !== undefined && (
                            <Text type={'h100'} data-testid={`${dataTestId}.subtotalAmount`}>
                                {t.cognate(formattedSubtotal)}
                            </Text>
                        )}
                        {formattedSubtotal !== undefined && (
                            <Text type={'b100'}>{t('estimatedSubtotalTaxShipping-text')}</Text>
                        )}
                        {formattedSubtotal === undefined && (
                            <Text type={'b200'}>{t('estimatedSubtotalUnavailable-text')}</Text>
                        )}
                    </Column>
                </Row>
            </Column>
        </Tile>
    );
};

export { PoReviewLineItemsTile };
